import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useUnresolvedVenueAccessSummary, useUsers, type User } from './api';
import { CreateUserDialog } from './CreateUserDialog';
import { ManageUserDialog } from './ManageUserDialog';
import { UserAssignments } from './UserAssignments';

const PAGE_SIZE = 20;

type Tab = 'all' | 'needs';

export function UsersPage() {
  const { t } = useTranslation('common');
  const canManage = useAuthorization().can('iam.user.manage');

  const [tab, setTab] = useState<Tab>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<User | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, isError } = useUsers({ search, page, pageSize: PAGE_SIZE });
  const summary = useUnresolvedVenueAccessSummary();

  if (!canManage) return <Forbidden />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const affectedUsers = summary.data?.affectedUsers ?? [];
  const affectedCount = summary.data?.affectedUserCount ?? 0;

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  }

  function toggleExpanded(id: string) {
    setExpanded((current) => (current === id ? null : id));
  }

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('users.title')}</h1>
        <Button onClick={() => setCreating(true)}>{t('users.actions.create')}</Button>
      </div>

      <div className="flex gap-1 border-b">
        <TabButton active={tab === 'all'} onClick={() => setTab('all')}>
          {t('users.tabs.all')}
        </TabButton>
        <TabButton active={tab === 'needs'} onClick={() => setTab('needs')}>
          {affectedCount > 0 && <AlertTriangle className="size-4 text-amber-500" />}
          {t('users.tabs.needs')}
          <span className={cn(affectedCount > 0 && 'text-amber-600')}>({affectedCount})</span>
        </TabButton>
      </div>

      {tab === 'all' && (
        <form onSubmit={applySearch} className="flex max-w-md gap-2">
          <Input
            aria-label={t('users.search.label')}
            placeholder={t('users.search.placeholder')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Button type="submit" variant="secondary">
            {t('users.search.submit')}
          </Button>
        </form>
      )}

      {tab === 'all' && (
        <>
          {isLoading && <p className="text-muted-foreground">{t('users.loading')}</p>}
          {isError && <p className="text-destructive">{t('users.errors.loadFailed')}</p>}

          {data && (
            <>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50 text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium">{t('users.columns.email')}</th>
                      <th className="px-4 py-2 font-medium">{t('users.columns.name')}</th>
                      <th className="px-4 py-2 font-medium">{t('users.columns.status')}</th>
                      <th className="px-4 py-2 font-medium">{t('users.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                          {t('users.empty')}
                        </td>
                      </tr>
                    )}
                    {data.items.map((user) => (
                      <UserRow
                        key={user.id}
                        userId={user.id}
                        email={user.email}
                        displayName={user.displayName}
                        status={
                          <span
                            className={user.isActive ? 'text-primary' : 'text-muted-foreground'}
                          >
                            {user.isActive ? t('users.status.active') : t('users.status.inactive')}
                          </span>
                        }
                        expanded={expanded === user.id}
                        onToggle={() => toggleExpanded(user.id)}
                        onManage={() => setManaging(user)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {t('users.pagination.summary', { page: page + 1, totalPages, total: data.total })}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    {t('users.pagination.previous')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page + 1 >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    {t('users.pagination.next')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'needs' && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('users.columns.email')}</th>
                <th className="px-4 py-2 font-medium">{t('users.columns.name')}</th>
                <th className="px-4 py-2 font-medium">{t('users.needs.column')}</th>
                <th className="px-4 py-2 font-medium">{t('users.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {affectedUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    {t('users.needs.empty')}
                  </td>
                </tr>
              )}
              {affectedUsers.map((user) => (
                <UserRow
                  key={user.userId}
                  userId={user.userId}
                  email={user.email}
                  displayName={user.displayName}
                  highlight
                  status={<span className="text-amber-600">{t('users.status.needsVenues')}</span>}
                  expanded={expanded === user.userId}
                  onToggle={() => toggleExpanded(user.userId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => toast.success(t('users.toast.created'))}
      />
      <ManageUserDialog
        user={managing}
        onOpenChange={(open) => !open && setManaging(null)}
        onActioned={(message) => toast.success(message)}
      />
    </section>
  );
}

function UserRow({
  userId,
  email,
  displayName,
  status,
  highlight,
  expanded,
  onToggle,
  onManage,
}: {
  userId: string;
  email: string;
  displayName: string | null;
  status: React.ReactNode;
  highlight?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onManage?: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <Fragment>
      <tr className={cn('border-b last:border-0', highlight && 'bg-amber-50/60')}>
        <td className="px-4 py-2">{email}</td>
        <td className="px-4 py-2">{displayName ?? '—'}</td>
        <td className="px-4 py-2">{status}</td>
        <td className="px-4 py-2">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onToggle}>
              {expanded ? t('users.needs.hideAccess') : t('users.needs.manageAccess')}
              <ChevronDown
                className={cn('size-4 transition-transform', expanded && 'rotate-180')}
              />
            </Button>
            {onManage && (
              <Button size="sm" variant="outline" onClick={onManage}>
                {t('users.actions.manage')}
              </Button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/20 last:border-0">
          <td colSpan={4} className="px-4 py-3">
            <UserAssignments userId={userId} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
